import { useEffect, useMemo, useRef } from 'react';
import { profile } from '../../../helpers/client';
import { useFormData, useFormStore } from '../../../store/selectors/formStore';
import { omit, pick, removeUndefined } from '../../../helpers';
import {
    DADATA_FORM_FIELDS_MAP,
    FIELDS_FOR_PLEDGE_AUTO,
    FIELDS_FOR_PLEDGE_REALTY,
    FORM_FIELDS,
    PROFILE_PEOPLE_FIELDS_MAP,
    REQUIRED_FIELDS_FOR_PLEDGE_AUTO_UPDATE,
    REQUIRED_FIELDS_FOR_PLEDGE_REALTY_UPDATE,
    REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE,
} from '../../../constants';
import { useIsPage } from '../../../store/selectors/selection';
import { extendFormData, extendFormStore } from '../../../store/reducers/formStore';
import { silentAuthorizeUser } from '../../../store/reducers/user';
import { getUserIdFromWlStorageItem, loadWlAuthorizedForm, saveWlAuthorizedForm } from './wl.local';
import { useDispatch } from 'react-redux';
import { clearSessionStorageFormData, getNewData } from './helpers';
import { useBoolean } from '@sravni/react-utils';
import clone from 'lodash/cloneDeep';

export const MANUAL_PROFILE_UPDATE_EVENT = 'manualProfileUpdate';

export const getProfileUpdateEvent = (detail?: Record<string, unknown>) =>
    new CustomEvent(MANUAL_PROFILE_UPDATE_EVENT, {
        bubbles: true,
        cancelable: false,
        detail,
    });

const checkProfileDataHasAllRequiredFields = (data: FE.FormData, requiredFields: string[]) => {
    return requiredFields.every((key) => data[key] || typeof data[key] === 'number');
};
const checkUpdateDataHasAnyRequiredFields = (formData: FE.FormData) => {
    return REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE.some((key) => formData[key] || typeof formData[key] === 'number');
};

const getRequiredFieldsData = (currentData: FE.FormData) => {
    return removeUndefined(pick(currentData, REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE), ['']);
};

/*
    Если в обновленных полях есть поле, связанное с адресом, то в обновление добавлять объект Dadata.
    Если объекта нет, то удалить поле из обновления (т к gql не сможет сохранить новые данные без обязательного fiasId)
 */
const prepareAddressesFields = (update: FE.FormData, currentForm: FE.FormData) => {
    const updateFields = Object.keys(update);

    Object.keys(DADATA_FORM_FIELDS_MAP).forEach((dadataField) => {
        if (DADATA_FORM_FIELDS_MAP[dadataField].some((addressField) => updateFields.includes(addressField))) {
            if (currentForm[dadataField]) {
                update[dadataField] = currentForm[dadataField];
            } else {
                DADATA_FORM_FIELDS_MAP[dadataField].forEach((removeField) => {
                    delete update[removeField];
                    delete currentForm[dadataField];
                });
            }
        }
    });
};

/*
    Если в обновленных полях залога нет обязательных, то удалять их из обновления
 */
const preparePledgeFields = (update: FE.FormData, currentForm: FE.FormData) => {
    const updateFields = Object.keys(update);
    const updatedAutoPledgeFields = FIELDS_FOR_PLEDGE_AUTO.filter((field) => updateFields.includes(field));
    const updatedRealtyPledgeFields = FIELDS_FOR_PLEDGE_REALTY.filter((field) => updateFields.includes(field));

    if (updatedAutoPledgeFields.length > 0) {
        const hasAllRequiredFields = checkProfileDataHasAllRequiredFields(
            currentForm,
            REQUIRED_FIELDS_FOR_PLEDGE_AUTO_UPDATE,
        );
        if (hasAllRequiredFields) {
            REQUIRED_FIELDS_FOR_PLEDGE_AUTO_UPDATE.forEach((requiredField) => {
                if (!update[requiredField]) {
                    update[requiredField] = currentForm[requiredField];
                }
            });
        } else {
            FIELDS_FOR_PLEDGE_AUTO.forEach((autoPledgeField) => {
                delete update[autoPledgeField];
                delete currentForm[autoPledgeField];
            });
        }
    }

    if (updatedRealtyPledgeFields.length > 0) {
        const hasAllRequiredFields = checkProfileDataHasAllRequiredFields(
            currentForm,
            REQUIRED_FIELDS_FOR_PLEDGE_REALTY_UPDATE,
        );
        if (hasAllRequiredFields) {
            REQUIRED_FIELDS_FOR_PLEDGE_REALTY_UPDATE.forEach((requiredField) => {
                if (!update[requiredField]) {
                    update[requiredField] = currentForm[requiredField];
                }
            });
        } else {
            FIELDS_FOR_PLEDGE_REALTY.forEach((realtyPledgeField) => delete update[realtyPledgeField]);
        }
    }
};

// TODO: [Profile Graphql] если в SessionStorage были сохранены данные и пользователь попал на выдачу после перезагрузки (autoRun), то сделать update в профиль (при успешном попадании на выдачу)
export const useProfile = (enabled = false, currentStep = 1, excludedFields: Array<keyof FE.FormData> = []) => {
    const { userId: userIdFromStorage, autoRun } = useFormStore();
    const formData = useFormData();
    const { isWlPage } = useIsPage();
    const [updateProfileTrigger, { toggle: toggleUpdateProfileTrigger, off: offUpdateProfileTrigger }] = useBoolean();
    const prevFormDataRef = useRef<FE.FormData>(removeUndefined(omit(formData, excludedFields), [''], true));
    const prevStepRef = useRef(currentStep);
    const isWlStorageLoaded = useRef(false);
    const formProfileMemoData = useMemo(() => pick(formData, Object.keys(PROFILE_PEOPLE_FIELDS_MAP)), [formData]);
    const dispatch = useDispatch();

    const updatePrevStep = (step: number) => {
        if (step !== prevStepRef.current) {
            prevStepRef.current = step;
        }
    };

    const update = (isTriggeredByEvent = false) => {
        if (!enabled) {
            return;
        }

        if (!userIdFromStorage || autoRun) {
            updatePrevStep(currentStep);
            return;
        }

        const formProfileData = clone(formProfileMemoData);

        const dataToSave = omit(getNewData(prevFormDataRef.current, formProfileData), excludedFields);

        if (Object.keys(dataToSave).length === 0) {
            updatePrevStep(currentStep);
            return;
        }

        prepareAddressesFields(dataToSave, formProfileData);
        preparePledgeFields(dataToSave, formProfileData);

        // для WL страниц при любом изменении поля дополнительно сохранять анкету в WL Session Storage
        if (isWlPage && formProfileData) {
            const wlData = removeUndefined(formData, [''], true);
            saveWlAuthorizedForm(wlData, userIdFromStorage);
        }

        if (
            Object.keys(dataToSave).length > 0 &&
            // в анкете заполнены обязательные поля firstName, lastName, birthDay
            checkProfileDataHasAllRequiredFields(formProfileData, REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE) &&
            // при вызове события обновления профиля ИЛИ при переходе на следующий шаг ИЛИ при изменении одного из обязательных полей
            (isTriggeredByEvent || currentStep > prevStepRef.current || checkUpdateDataHasAnyRequiredFields(dataToSave))
        ) {
            const updateData = checkUpdateDataHasAnyRequiredFields(dataToSave)
                ? removeUndefined(formProfileData, [''], true)
                : { ...getRequiredFieldsData(formProfileData), ...dataToSave };

            prevFormDataRef.current = { ...formProfileMemoData };
            profile.update(updateData, userIdFromStorage).then((isUpdated) => {
                // При успешном сохранении данных очищать Session Storage
                if (isUpdated) {
                    clearSessionStorageFormData();
                }
            });
        }

        updatePrevStep(currentStep);
    };

    const loadFromWlSessionStorage = () => {
        if (!enabled || !isWlPage || isWlStorageLoaded.current) {
            return;
        }
        const wlUserId = getUserIdFromWlStorageItem();
        if (!wlUserId) {
            return;
        }
        const dataParsed = loadWlAuthorizedForm(wlUserId);
        if (!dataParsed || !dataParsed[FORM_FIELDS.PHONE]) {
            return;
        }

        const data = removeUndefined(dataParsed, [''], true);
        prevFormDataRef.current = data;
        isWlStorageLoaded.current = true;
        dispatch(extendFormData(data));
        dispatch(silentAuthorizeUser({ sub: wlUserId }));
        dispatch(extendFormStore({ userId: wlUserId, autoRun: true }));
    };

    useEffect(() => {
        // TODO: [Profile Graphql] разобраться, как иначе сохранять данные в профиль на выдаче (при изменении фильтров / полей залога авто и недвижки)
        document.addEventListener(MANUAL_PROFILE_UPDATE_EVENT, toggleUpdateProfileTrigger, false);
        return () => {
            document.removeEventListener(MANUAL_PROFILE_UPDATE_EVENT, toggleUpdateProfileTrigger, false);
        };
    }, []);

    // загрузка данных из SessionStorage для WL страниц
    useEffect(() => {
        loadFromWlSessionStorage();
    }, [enabled, dispatch, isWlPage]);

    // сохранение данных в Profile Service
    useEffect(() => {
        update();
    }, [formProfileMemoData, enabled, currentStep, excludedFields, userIdFromStorage, isWlPage]);

    useEffect(() => {
        if (updateProfileTrigger) {
            update(true);
            offUpdateProfileTrigger();
        }
    }, [updateProfileTrigger]);
};
